//@ts-nocheck
import RulesDataContext from "contexts/RulesContext";
import { isEmpty } from "lodash";
import { ConstValues } from "./constValues";
import { useContext } from "react";

// ─── Type Definitions ────────────────────────────────────────────────

declare global {
  interface Array<T> {
    countSameItems(a: string[]): number;
  }
}

export interface OrganisationSettings {
  type?: number;
  isLicensed: boolean;
  GSMOnly: boolean;
  activated: boolean;
  allowsJobRequisition: boolean;
  allowsOnboarding: boolean;
  allowsSearchAndMatch: boolean;
  [key: string]: unknown;
}

export interface Role {
  name: string;
  [key: string]: unknown;
}

export interface PermissionGroup {
  roles: Role[];
  [key: string]: unknown;
}

export interface EmployeeGroup {
  id: string;
  permissionGroups: PermissionGroup[];
  [key: string]: unknown;
}

export interface EmployeeAuthorisation {
  id: string;
  companyAdmin: boolean;
  payrollUser: boolean;
  suspended: boolean;
  agencyUser: boolean;
  talentpoolAccess: boolean;
  employeeGroups: EmployeeGroup[];
  [key: string]: unknown;
}

export interface CompanyDetails {
  id: string;
  [key: string]: unknown;
}

export interface RulesDetails {
  organisationSettings: OrganisationSettings;
  employeeAuthorisation: EmployeeAuthorisation;
  companyDetails: CompanyDetails;
  reportAccessList: string[];
  paymentMethodType: number;
  updateRulesDetails?: (details: Partial<RulesDetails>) => void;
  [key: string]: unknown;
}

export interface TableRow {
  editable?: boolean;
  currentState?: string;
  [key: string]: unknown;
}

export interface TableRowActions {
  [key: string]: ((row: TableRow) => boolean) | ((...args: unknown[]) => void);
}

export interface KeycloakInstance {
  authenticated: boolean;
  idToken: string | undefined;
  logout: (options: { redirectUri: string }) => void;
}

export interface ApplicationState {
  name: string;
  [key: string]: unknown;
}

// ─── Constants ───────────────────────────────────────────────────────

const ERROR_CODES: string[] = [
  "400",
  "401",
  "402",
  "403",
  "404",
  "405",
  "406",
  "407",
  "408",
  "409",
  "410",
  "411",
  "412",
  "413",
  "414",
  "415",
  "416",
  "417",
  "418",
  "422",
  "423",
  "424",
  "425",
  "426",
  "449",
  "450",
  "500",
  "501",
  "502",
  "503",
  "504",
  "505",
  "506",
  "507",
  "509",
  "510",
];

const AGENCY_ALLOWED_URLS: string[] = ["maintainusers", "tags", "candidatelists"];
const AGENCY_BLOCKED_URLS: string[] = [
  "dashboard",
  "extrainformation",
  "tokens",
  "questionnaires",
  "screening",
  "emailtemplate",
  "jobboardbrand",
  "searchandmatch",
  "organisationagencies",
  "brands",
  "subsites",
  "applicationprocess",
  "about",
  "externaljobboards",
  "employmentagreement",
  "jobrequisition",
  "compensationitems",
  "declinationreasons",
  "emaildomainverification",
  "documentcategories",
  "hierarchyroles",
  "hierarchies",
  "reports",
  "reportusers",
  "integrations",
  "modules",
];

// Add agency-specific constants
const AGENCY_RESTRICTED_ACTIONS: string[] = [
  "edit",
  "delete",
  "archive",
  "activate",
  "copy",
  "duplicate",
  "approve",
  "reject",
  "publish",
  "unpublish",
  "manage",
  "create",
  "add",
  "remove",
  "update",
  "modify",
  // "changepanelmembers",
];

const AGENCY_ALLOWED_ACTIONS: string[] = ["view", "changepanelmembers"];

// ─── Utility functions ──────────────────────────────────────────────

export function getUrlPartsArray(url: string | null): string[] {
  return url === null ? [""] : url.toLowerCase().indexOf("/") !== -1 ? url.split("/") : [String(url)];
}

// Extend Array prototype
Array.prototype.countSameItems = function (a: string[]): number {
  return this.map((v: string) => v.toLowerCase()).filter((i: string) => {
    return a.map((v: string) => v.toLowerCase()).indexOf(i) !== -1;
  }).length;
};

// ─── Core auth functions ────────────────────────────────────────────

export function getRules(): RulesDetails | null {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rulesDetails = useContext(RulesDataContext);
  return isEmpty(rulesDetails) ? null : (rulesDetails as RulesDetails);
}

export function handleAuthDecision(decision: boolean, redirectToErrorPage: boolean = true): boolean {
  if (!decision && redirectToErrorPage) {
    window.location.href = "/403?detail=&message=Access+denied!+You+do+not+have+access+to+this+page.";
  }
  return decision;
}

export function isAgency(rulesDetails: RulesDetails | null = null): boolean {
  return rulesDetails?.organisationSettings?.type === ConstValues.OrganisationType.AGENCY;
}

export function isAgencyUser(rulesDetails: RulesDetails | null = null): boolean {
  return rulesDetails?.organisationSettings?.type === ConstValues.OrganisationType.AGENCY && isAgent(rulesDetails);
}

export function isAgent(rulesDetails: RulesDetails | null = null): boolean | undefined {
  return getCurrentEmployeeAuthorisation(rulesDetails)?.agencyUser;
}

// ─── Permission checking functions ──────────────────────────────────

const checkAgencyAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (!isAgency(rules)) {
    return true;
  }

  if (isAgency(rules)) {
    if (AGENCY_ALLOWED_URLS.countSameItems(urlParts) > 0) {
      return true;
    }
    // Block specific URLs
    if (AGENCY_BLOCKED_URLS.countSameItems(urlParts) > 0) {
      return false;
    }
  }

  // Allow dashboard access

  return true;
};

const checkAdminAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (!rules?.employeeAuthorisation?.companyAdmin && ["admin"].countSameItems(urlParts) > 0) {
    return false;
  }
  return true;
};

const checkDashboardAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (
    !rules.organisationSettings.isLicensed &&
    !rules?.employeeAuthorisation?.companyAdmin &&
    ["dashboard"].countSameItems(urlParts) > 0
  ) {
    return false;
  }
  return true;
};

const checkSuspendedUser = (rules: RulesDetails): boolean => {
  return !rules?.employeeAuthorisation?.suspended;
};

const checkJobRequisitionAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (!rules.organisationSettings.allowsJobRequisition && ["jobrequisition"].countSameItems(urlParts) > 0) {
    return false;
  }

  if (rules.organisationSettings.allowsJobRequisition && ["jobs", "add"].countSameItems(urlParts) > 1) {
    return false;
  }

  return true;
};

const checkTalentPoolAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (
    ((rules.organisationSettings.isLicensed && !rules?.employeeAuthorisation?.talentpoolAccess) ||
      !rules.organisationSettings.isLicensed) &&
    !rules.organisationSettings.GSMOnly &&
    ["talentpool"].countSameItems(urlParts) > 0
  ) {
    return false;
  }
  return true;
};

const checkLicenseBasedAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  const { isLicensed, GSMOnly } = rules.organisationSettings;

  // Subsites
  if ((!isLicensed || GSMOnly) && ["admin", "subsites"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Application process
  if (GSMOnly && ["admin", "applicationprocess"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Security and access
  if (
    !isLicensed &&
    ["admin", "hierarchyroles", "hierarchies", "employmentagreement", "extrainformation", "tokens"].countSameItems(
      urlParts,
    ) > 1
  ) {
    return false;
  }

  // Modules and integrations
  if (
    !isLicensed &&
    !GSMOnly &&
    !isAgency(rules) &&
    ["admin", "maintainusers", "modules", "integrations"].countSameItems(urlParts) > 1
  ) {
    return false;
  }

  return true;
};

const checkQuestionnaireAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  const { isLicensed, GSMOnly } = rules.organisationSettings;

  // Organisation questionnaires
  if ((!isLicensed || GSMOnly) && ["admin", "questionnaires", "organisation"].countSameItems(urlParts) > 2) {
    return false;
  }

  // Confidential questionnaires
  if ((!isLicensed || GSMOnly) && ["admin", "questionnaires", "confidential"].countSameItems(urlParts) > 2) {
    return false;
  }

  if (!isLicensed && ["admin", "questionnaires", "confidential"].countSameItems(urlParts) > 2) {
    return false;
  }

  return true;
};

const checkOnboardingAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (!rules.organisationSettings.allowsOnboarding && ["admin", "compensationitems"].countSameItems(urlParts) > 1) {
    return false;
  }
  return true;
};

const checkMiscAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  const { isLicensed, GSMOnly } = rules.organisationSettings;

  // Tags
  if (!isLicensed && !GSMOnly && !isAgencyUser(rules) && ["admin", "tags"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Candidate lists
  if (!isLicensed && !GSMOnly && !isAgencyUser(rules) && ["admin", "candidatelists"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Organisation reports
  if (!isLicensed && ["admin", "reports"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Report users
  if ((!isLicensed || GSMOnly) && ["admin", "reportusers"].countSameItems(urlParts) > 1) {
    return false;
  }

  // Marketplace
  if (GSMOnly && ["admin", "marketplace"].countSameItems(urlParts) > 1) {
    return false;
  }

  return true;
};

const checkReportAccess = (rules: RulesDetails, urlParts: string[], rulesDetails: RulesDetails | null): boolean => {
  if (
    (["report"].countSameItems(urlParts) > 0 || ["reports"].countSameItems(urlParts) > 0) &&
    (!rules.organisationSettings.isLicensed || !userAllowedToViewReports(rules.reportAccessList, rules)) &&
    !isGSMAdmin(rulesDetails)
  ) {
    return false;
  }
  return true;
};

const checkSearchAndMatchAccess = (rules: RulesDetails, urlParts: string[]): boolean => {
  if (["searchandmatch"].countSameItems(urlParts) === 0) {
    return true;
  }
  if (rules.employeeAuthorisation?.suspended || isAgencyUser(rules)) {
    return false;
  }
  return (
    userIsAdminOrInRoleAllHierarchies("searchandmatch", rules) || !!rules.organisationSettings?.GSMOnly
  );
};

const checkGSMOnlyRestrictions = (rules: RulesDetails, urlParts: string[]): boolean => {
  const { GSMOnly } = rules.organisationSettings;

  if (!GSMOnly) return true;

  const restrictedPaths: string[][] = [
    ["admin", "jobboardbrand"],
    ["admin", "questionnaires"],
    ["admin", "organisationagencies"],
    ["admin", "externaljobboards"],
    ["admin", "compensationitems"],
    ["admin", "declinationreasons"],
    ["admin", "documentcategories"],
  ];

  return !restrictedPaths.some((path: string[]) => path.countSameItems(urlParts) > 1);
};

// Helper function to check agency action restrictions
const checkAgencyActionRestriction = (action: string, rulesDetails: RulesDetails | null): boolean => {
  if (!isAgencyUser(rulesDetails)) {
    return false; // Not an agency user, allow all actions
  }

  const lc_action = action.toLowerCase();
  if (AGENCY_ALLOWED_ACTIONS.includes(lc_action)) {
    return true;
  }

  // Block restricted actions for agency users
  if (AGENCY_RESTRICTED_ACTIONS.includes(lc_action)) {
    return false;
  }

  // Default: allow action if not in restricted list
  return true;
};

// ─── Main URL permission function ───────────────────────────────────
const isCasualUser = (rulesDetails: RulesDetails | null, urlParts: string[]) => {
  let isLicensed = false;
  if (rulesDetails?.organisationSettings?.isLicensed) {
    isLicensed = true;
  }
  const isGSMOnlyOrg = rulesDetails?.organisationSettings?.GSMOnly ?? false;
    if (isLicensed || isGSMOnlyOrg) return true;

  const restrictedPaths: string[][] = [
    ["admin", "organisationagencies"],
    ["admin", "modules"],
    ["admin", "maintainusers"],
    ["admin", "integrations"],
    ["admin", "organisationagencies"],
    ["admin", "externaljobboards"],
    ["admin", "compensationitems"],
    ["admin", "declinationreasons"],
    ["admin", "documentcategories"],
  ];

  return !restrictedPaths.some((path: string[]) => path.countSameItems(urlParts) > 1);
};

export function isUrlAllowed(
  url: string | null,
  redirectToErrorPage: boolean = false,
  rulesDetails: RulesDetails | null = null,
): boolean {
  const rules = rulesDetails || getRules();
  if (rules === null) {
    return false;
  }

  const urlParts = getUrlPartsArray(url);

  // Run all permission checks
  const permissionChecks: { name: string; fn: () => boolean }[] = [
    { name: "checkAgencyAccess", fn: () => checkAgencyAccess(rules, urlParts) },
    { name: "checkAdminAccess", fn: () => checkAdminAccess(rules, urlParts) },
    { name: "checkDashboardAccess", fn: () => checkDashboardAccess(rules, urlParts) },
    { name: "checkSuspendedUser", fn: () => checkSuspendedUser(rules) },
    { name: "checkJobRequisitionAccess", fn: () => checkJobRequisitionAccess(rules, urlParts) },
    { name: "checkTalentPoolAccess", fn: () => checkTalentPoolAccess(rules, urlParts) },
    { name: "checkLicenseBasedAccess", fn: () => checkLicenseBasedAccess(rules, urlParts) },
    { name: "checkQuestionnaireAccess", fn: () => checkQuestionnaireAccess(rules, urlParts) },
    { name: "checkOnboardingAccess", fn: () => checkOnboardingAccess(rules, urlParts) },
    { name: "checkMiscAccess", fn: () => checkMiscAccess(rules, urlParts) },
    { name: "checkReportAccess", fn: () => checkReportAccess(rules, urlParts, rulesDetails) },
    { name: "checkSearchAndMatchAccess", fn: () => checkSearchAndMatchAccess(rules, urlParts) },
    { name: "checkGSMOnlyRestrictions", fn: () => checkGSMOnlyRestrictions(rules, urlParts) },
    { name: "CasualUserAccess", fn: () => isCasualUser(rules, urlParts) },
  ];

  const decision = permissionChecks.every((check: { name: string; fn: () => boolean }) => {
    const result = check.fn();
    // if (!result) {
    //   console.log(`Permission check failed: ${check.name} - URL Parts: ${urlParts.join("/")}`);
    // }
    return result;
  });

  return handleAuthDecision(decision, redirectToErrorPage);
}

// ─── UI Section permission checks ──────────────────────────────────

const checkJobRequisitionUIAccess = (rules: RulesDetails, uiSections: string[]): boolean => {
  if (
    !rules?.organisationSettings?.allowsJobRequisition &&
    ["AdminRoleJobRequisition"].countSameItems(uiSections) > 0
  ) {
    return false;
  }
  return true;
};

const checkJobUIAccess = (rules: RulesDetails, uiSections: string[]): boolean => {
  if (!rules?.employeeAuthorisation?.companyAdmin && ["AdminRoleJob"].countSameItems(uiSections) > 0) {
    return false;
  }
  return true;
};
const checkJobAddAccess = (_rules: RulesDetails, _uiSections: string[]): boolean => {
  //add job allowed if job req for company
  // if (rules.organisationSettings.allowsJobRequisition && ["AdminAddJob"].countSameItems(uiSections) > 0) {
  //   decision = false;
  // }
  return true;
};

const checkOnboardingUIAccess = (rules: RulesDetails, uiSections: string[]): boolean => {
  if (
    !rules.organisationSettings?.allowsOnboarding &&
    ["onboarding-bucket", "approve-job-package"].countSameItems(uiSections) > 0
  ) {
    return false;
  }
  return true;
};

export function isUISectionAllowed(
  uiSections: string[],
  _url: string | null = null,
  rulesDetails: RulesDetails | null = null,
): boolean {
  const rules = rulesDetails || getRules();
  if (rules === null) {
    return false;
  }
  if (!Array.isArray(uiSections) || uiSections.length < 1) {
    return true;
  }
  const uiChecks: (() => boolean)[] = [
    () => checkJobRequisitionUIAccess(rules, uiSections),
    () => checkJobUIAccess(rules, uiSections),
    () => checkJobAddAccess(rules, uiSections),
    () => checkOnboardingUIAccess(rules, uiSections),
  ];

  return uiChecks.every((check: () => boolean) => check());
}

// ─── Table action permissions ───────────────────────────────────────

export function isRowEditable(row: TableRow): boolean {
  return Object.prototype.hasOwnProperty.call(row, "editable") ? (row.editable as boolean) : true;
}

export function isTableActionAllowed(
  action: string,
  i: number,
  row: TableRow,
  tableRowActions: TableRowActions,
  rulesDetails: RulesDetails | null = null,
): boolean {
  const lc_action = action.toLowerCase();
  const viewActions: string[] = ["view", "copyurl", "viewjobreq", "applications"];
  if (checkAgencyActionRestriction(action, rulesDetails)) {
    return true;
  }

  // Existing panel member logic
  if (lc_action === "changepanelmembers" && row.currentState === "active") {
    return true;
  } else if (lc_action === "changepanelmembers" && (row.currentState === "archived" || row.currentState === "draft")) {
    return false;
  }

  const isViewOrCopyUrl = viewActions.indexOf(lc_action) !== -1;
  const isNotCopyAndEditable = isRowEditable(row) && lc_action !== "copy";
  const isCopyAllowed =
    lc_action === "copy" && isRowEditable(row) && isUISectionAllowed(["AdminCopyJob"], null, rulesDetails);
  const tableRowsConditions =
    (!Object.prototype.hasOwnProperty.call(tableRowActions, "isShow" + action) ||
      (tableRowActions["isShow" + action] as (row: TableRow) => boolean)(row)) &&
    Object.prototype.hasOwnProperty.call(tableRowActions, "handle" + action);

  return (isViewOrCopyUrl || isNotCopyAndEditable || isCopyAllowed) && tableRowsConditions;
}

// ─── User role and permission functions ─────────────────────────────

export function currentUserIsCompanyAdmin(rulesDetails: RulesDetails | null = null): boolean | undefined {
  return getCurrentEmployeeAuthorisation(rulesDetails)?.companyAdmin;
}

export function getCurrentEmployeeAuthorisation(
  rulesDetails: RulesDetails | null = null,
): EmployeeAuthorisation | null {
  const rules = rulesDetails || getRules();
  return rules ? rules?.employeeAuthorisation : null;
}

export function getCurrentEmployeeAuthorisationId(rulesDetails: RulesDetails | null = null): string | null {
  const rules = rulesDetails || getRules();
  return rules ? rules?.employeeAuthorisation.id : null;
}

export function getCurrentCompany(): CompanyDetails | null {
  const rules = getRules();
  return rules ? rules?.companyDetails : null;
}

export function getCurrentCompanyId(rulesDetails: RulesDetails | null = null): string | null {
  const rules = rulesDetails || getRules();
  return rules ? rules?.companyDetails?.id : localStorage.getItem("company-id");
}

export function isCompanyActivated(): boolean {
  const rules = getRules();
  return rules ? rules.organisationSettings.activated : false;
}

// ─── Role-based access functions ────────────────────────────────────

const findRoleInEmployeeGroup = (employeeGroups: EmployeeGroup | null, roleName: string): boolean => {
  if (employeeGroups && employeeGroups?.permissionGroups) {
    const role = employeeGroups.permissionGroups.find((permissionGroup: PermissionGroup) =>
      permissionGroup.roles.some((roleItem: Role) => roleItem.name === roleName),
    );
    return !!role;
  }
  return false;
};

const getParentStateName = (fullStateName: string): string => {
  if (!fullStateName) {
    return fullStateName;
  }

  const parentRoleIndex = fullStateName.indexOf(".");
  if (parentRoleIndex !== -1) {
    fullStateName = fullStateName.substring(0, parentRoleIndex);
  }
  fullStateName = fullStateName.replace(/\d+$/, "");
  if (fullStateName === "contract") {
    return "onboarding";
  }
  return fullStateName;
};

const userIsInReportAccessList = (reportAccessList: string[] | null, rules: RulesDetails | null = null): boolean => {
  const employeeAuthorisation = getCurrentEmployeeAuthorisation(rules);
  return (
    employeeAuthorisation!.companyAdmin ||
    employeeAuthorisation!.payrollUser ||
    (reportAccessList !== null && reportAccessList.includes(employeeAuthorisation!.id))
  );
};

export function userIsAdminOrInRole(
  roleName: string,
  hierarchyId: string,
  rulesDetails: RulesDetails | null = null,
): boolean {
  const employeeAuthorisation = getCurrentEmployeeAuthorisation(rulesDetails);

  if (employeeAuthorisation?.companyAdmin || employeeAuthorisation?.payrollUser) {
    return true;
  }

  const filteredEmployeeGroup: EmployeeGroup | null | undefined =
    employeeAuthorisation && Object.prototype.hasOwnProperty.call(employeeAuthorisation, "employeeGroups")
      ? employeeAuthorisation.employeeGroups.find((g: EmployeeGroup) => g.id === hierarchyId)
      : null;

  return findRoleInEmployeeGroup(filteredEmployeeGroup ?? null, roleName);
}
export function userIsAgentOrHasAccessToJob(roleName, hierarchyId, rulesDetails = null) {
  const employeeAuthorisation = getCurrentEmployeeAuthorisation(rulesDetails);

  const filteredEmployeeGroup =
    employeeAuthorisation && Object.prototype.hasOwnProperty.call(employeeAuthorisation, "employeeGroups")
      ? employeeAuthorisation.employeeGroups.find((g) => g.id === hierarchyId)
      : null;

  return findRoleInEmployeeGroup(filteredEmployeeGroup, roleName) && isAgent(rulesDetails);
}

export function userIsAdminOrInRoleAllHierarchies(roleName: string, rulesDetails: RulesDetails | null = null): boolean {
  if (currentUserIsCompanyAdmin(rulesDetails)) {
    return true;
  }

  const employeeAuthorisation = getCurrentEmployeeAuthorisation(rulesDetails);
  const employeeGroups: EmployeeGroup[] | null =
    employeeAuthorisation && Object.prototype.hasOwnProperty.call(employeeAuthorisation, "employeeGroups")
      ? employeeAuthorisation.employeeGroups
      : null;

  if (employeeGroups && employeeGroups.length) {
    const result = employeeGroups.find((eg: EmployeeGroup) =>
      eg.permissionGroups.some((pg: PermissionGroup) => pg.roles.some((role: Role) => role.name === roleName)),
    );
    return !!result;
  }

  return false;
}

export function userAllowedToManageAnyApplicationState(
  hierarchyId: string,
  applicationStates: ApplicationState[],
  rulesDetails: RulesDetails | null = null,
): boolean {
  const employeeAuthorisation = getCurrentEmployeeAuthorisation(rulesDetails);

  if (employeeAuthorisation?.companyAdmin || employeeAuthorisation?.payrollUser) {
    return true;
  }

  const roleNames: string[] = applicationStates.map(
    (s: ApplicationState) => "manage-" + getParentStateName(s.name) + "-bucket",
  );

  const employeeGroup: EmployeeGroup | null =
    employeeAuthorisation?.employeeGroups?.find((g: EmployeeGroup) => g.id === hierarchyId) || null;

  if (employeeGroup?.permissionGroups) {
    const matchedGroup = employeeGroup.permissionGroups.find((permissionGroup: PermissionGroup) =>
      permissionGroup.roles.some((role: Role) => roleNames.includes(role.name)),
    );
    return !!matchedGroup;
  }

  return false;
}

// ─── Specific permission functions ──────────────────────────────────

export function userAllowedToManageApplicationState(
  hierarchyId: string,
  applicationState: string,
  rulesDetails: RulesDetails | null = null,
): boolean {
  return userIsAdminOrInRole("manage-" + getParentStateName(applicationState) + "-bucket", hierarchyId, rulesDetails);
}

export function userAllowedToViewApplicationState(
  hierarchyId: string,
  applicationState: string,
  rulesDetails: RulesDetails | null = null,
): boolean {
  return userIsAdminOrInRole("view-" + getParentStateName(applicationState) + "-bucket", hierarchyId, rulesDetails);
}

export function userAllowedToSendCommsFor(hierarchyId: string, rulesDetails: RulesDetails | null = null): boolean {
  return userIsAdminOrInRole("communication", hierarchyId, rulesDetails);
}

export function userAllowedToMaintainExternalJobBoards(
  hierarchyId: string,
  rulesDetails: RulesDetails | null = null,
): boolean {
  return userIsAdminOrInRole("edit-job", hierarchyId, rulesDetails);
}

export function userAllowedToOfferApprovalFor(hierarchyId: string, rulesDetails: RulesDetails | null = null): boolean {
  return userIsAdminOrInRole("approve-job-package", hierarchyId, rulesDetails);
}

export function userAllowedToViewReports(
  reportAccessList: string[] | null,
  rules: RulesDetails | null = null,
): boolean {
  return userIsInReportAccessList(reportAccessList, rules);
}

// ─── Organization type functions ────────────────────────────────────

export function isGSMOnlyOrg(inputRules: RulesDetails | null = null): boolean {
  const rules = inputRules ? inputRules : getRules();
  if (rules === null) {
    return false;
  }

  return !rules?.employeeAuthorisation?.suspended && rules.organisationSettings && rules.organisationSettings.GSMOnly;
}

export function isGSMAdmin(rulesDetails: RulesDetails | null = null): boolean | undefined {
  const rules = rulesDetails || getRules();
  return rules?.organisationSettings.GSMOnly && rules?.employeeAuthorisation?.companyAdmin;
}

export function isInvoiceOrg(rulesDetails: RulesDetails | null = null): boolean {
  const rules = rulesDetails || getRules();
  if (rules === null) {
    return false;
  }

  return !rules?.employeeAuthorisation?.suspended && rules.paymentMethodType !== null && rules.paymentMethodType === 1;
}

// ─── Utility functions ──────────────────────────────────────────────

export function isErrorPage(url: string): boolean {
  if (url === "/no-organisation") {
    return true;
  }
  return !!url && ERROR_CODES.indexOf(url.replace("/", "")) !== -1;
}

export function getRulesDetails(
  companyDetails: CompanyDetails,
  organization: {
    organisationSettings?: OrganisationSettings;
    reportAccess?: string[];
    paymentMethodType?: number;
  } | null,
  employeeAuthorisationData: EmployeeAuthorisation | null = null,
): RulesDetails | null {
  const rules: Partial<RulesDetails> = {};
  const organisationSettings = organization?.organisationSettings;
  const reportAccessList = organization?.reportAccess;
  const paymentMethodType = organization?.paymentMethodType;

  if (companyDetails && organization) {
    if (typeof employeeAuthorisationData !== "undefined") {
      rules.employeeAuthorisation = employeeAuthorisationData as EmployeeAuthorisation;
    }
    if (typeof companyDetails !== "undefined") {
      rules.companyDetails = companyDetails;
    }
    if (typeof organisationSettings !== "undefined") {
      rules.organisationSettings = organisationSettings;
    }
    if (typeof reportAccessList !== "undefined") {
      rules.reportAccessList = reportAccessList;
    }
    if (typeof paymentMethodType !== "undefined") {
      rules.paymentMethodType = paymentMethodType;
    }
  }

  return isEmpty(rules) ? null : (rules as RulesDetails);
}

export const handleLogout = (keycloak: KeycloakInstance, locationOrigin: string = window.location.origin): void => {
  localStorage.removeItem("checkSubscription");
  localStorage.removeItem("company-id");
  localStorage.removeItem("company-name");
  if (keycloak.authenticated && keycloak.idToken) {
    keycloak.logout({ redirectUri: locationOrigin });
  }
};
